import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Eye, Download, ArrowLeft, UserCheck, Loader2 } from "lucide-react";
import { api, pdfUrl, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const GRADE_SCALE_TEXT = "A+ Above 85% · A 75–84% · B+ 65–74% · B 50–64% · C Below 50%";

function gradeFor(pct) {
  if (pct >= 85) return "A+";
  if (pct >= 75) return "A";
  if (pct >= 65) return "B+";
  if (pct >= 50) return "B";
  return "C";
}

export default function MarksheetStudio({ sid, preStudentId }) {
  const nav = useNavigate();
  const [courses, setCourses] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [master, setMaster] = useState(null);
  const [marks, setMarks] = useState([]);
  const [existing, setExisting] = useState(null);
  const [preview, setPreview] = useState({ open: false, url: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!(sid || preStudentId));

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  const buildRows = (m, existingMarks, courseList) => {
    const course = (courseList || courses).find((c) => c.id === m.course_id);
    const bySubject = {};
    (existingMarks || []).forEach((x) => { bySubject[x.subject] = x; });
    const rows = (course?.subjects || []).map((s, i) => ({
      id: `${s.id || i}`,
      subject: s.name,
      term: s.term ?? 1,
      total: bySubject[s.name]?.total ?? 100,
      min: bySubject[s.name]?.min ?? 35,
      obtained: bySubject[s.name]?.obtained ?? "",
    }));
    (existingMarks || []).forEach((x) => {
      if (!rows.find((r) => r.subject === x.subject)) {
        rows.push({
          id: `x-${x.subject}`,
          subject: x.subject,
          term: x.term ?? 1,
          total: x.total ?? 100,
          min: x.min ?? 35,
          obtained: x.obtained ?? "",
        });
      }
    });
    return rows;
  };

  const selectById = async (id, courseList) => {
    const { data: m } = await api.get(`/certificates/${id}`);
    if (Number(m.format) === 2) {
      toast.error("Select a Format 1 certificate record, not a marksheet");
      return;
    }
    setMaster(m);
    setResults([]);
    setQuery("");
    const { data: linked } = await api.get("/certificates", { params: { student_id: id } });
    const ex = linked.find((x) => Number(x.format) === 2);
    setExisting(ex || null);
    setMarks(buildRows(m, ex?.marks || [], courseList));
  };

  useEffect(() => {
    if (master || courses.length === 0) return;
    if (!sid && !preStudentId) return;
    (async () => {
      try {
        if (sid) {
          const { data: ms } = await api.get(`/certificates/${sid}`);
          setExisting(ms);
          if (ms.student_id) {
            const { data: m } = await api.get(`/certificates/${ms.student_id}`);
            setMaster(m);
            setMarks(buildRows(m, ms.marks || []));
          } else {
            setMaster(ms); // legacy standalone marksheet
            setMarks(buildRows(ms, ms.marks || []));
          }
        } else if (preStudentId) {
          await selectById(preStudentId, courses);
        }
      } catch {
        toast.error("Failed to load record");
      } finally {
        setLoading(false);
      }
    })();
     
  }, [sid, preStudentId, courses]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/certificates/masters", { params: { q: query } });
        setResults(data);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const totals = useMemo(() => {
    const t = marks.reduce((a, m) => a + (parseFloat(m.total) || 0), 0);
    const o = marks.reduce((a, m) => a + (parseFloat(m.obtained) || 0), 0);
    const pct = t ? Math.round((o / t) * 10000) / 100 : 0;
    const failed = marks.some(
      (m) => m.obtained !== "" && (parseFloat(m.obtained) || 0) < (parseFloat(m.min) || 0)
    );
    return { t, o, pct, grade: gradeFor(pct), status: failed ? "FAIL" : "PASS" };
  }, [marks]);

  const mappedMarks = () =>
    marks.map((m) => ({
      subject: m.subject,
      term: m.term ?? 1,
      total: Number(m.total) || 0,
      min: Number(m.min) || 0,
      obtained: Number(m.obtained) || 0,
    }));

  const validate = () => {
    if (!master) {
      toast.error("Select a student first");
      return false;
    }
    if (marks.length === 0) {
      toast.error("No subjects configured for this student's course");
      return false;
    }
    if (marks.some((m) => m.obtained === "" || m.obtained === null)) {
      toast.error("Enter marks obtained for every subject");
      return false;
    }
    return true;
  };

  const doSave = async (openAfter = true) => {
    if (!validate()) return;
    setSaving(true);
    try {
      let saved;
      if (existing && Number(existing.format) === 2 && !existing.student_id) {
        // legacy standalone marksheet — update in place
        const payload = {
          ...master,
          marks: mappedMarks(),
          total_marks: totals.t,
          marks_obtained: totals.o,
          percentage: totals.pct,
          grade: totals.grade,
          result: totals.status,
        };
        const { data } = await api.put(`/certificates/${existing.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/marksheets", {
          student_id: master.id,
          marks: mappedMarks(),
        });
        saved = data;
      }
      toast.success(`Marksheet saved — ${totals.o}/${totals.t} (${totals.pct}%, ${totals.grade})`);
      if (openAfter) window.open(pdfUrl(saved.id), "_blank");
      nav("/admin/records");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doPreview = async () => {
    if (!validate()) return;
    try {
      const payload = {
        ...(existing || {}),
        ...master,
        format: 2,
        student_id: master.student_id ? master.student_id : master.id,
        marks: mappedMarks(),
        total_marks: totals.t,
        marks_obtained: totals.o,
        percentage: totals.pct,
        grade: totals.grade,
        result: totals.status,
        qr_code: existing?.qr_code || null,
      };
      const { data } = await api.post(
        "/certificates/preview",
        { student: payload },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      setPreview({ open: true, url });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    }
  };

  return (
    <div className="p-8 lg:p-10 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={() => nav(-1)}
            className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1">
            <ArrowLeft size={14} /> Back
          </button>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">
            {sid || existing ? "Edit Marksheet" : "Create Marksheet — Format 2"}
          </h1>
          <p className="text-slate-500 mt-1">
            Select an existing student — only marks obtained are needed. Everything else comes from the Format 1 record.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="ms-preview-btn" onClick={doPreview}
            disabled={!master} className="h-10">
            <Eye size={16} className="mr-2" /> Preview
          </Button>
          <Button data-testid="ms-generate-btn" onClick={() => doSave(true)}
            disabled={saving || !master} className="h-10 bg-blue-800 hover:bg-blue-900">
            <Download size={16} className="mr-2" />
            {saving ? "Saving…" : "Generate Marksheet"}
          </Button>
        </div>
      </div>

      {loading && <div className="text-slate-400 text-sm py-10 text-center">Loading…</div>}

      {!loading && !master && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 max-w-2xl">
          <Label className="text-sm text-slate-700">Find student (Format 1 record)</Label>
          <div className="relative mt-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="ms-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by registration no, enrollment no, certificate no or name…"
              className="pl-9 h-11"
              autoFocus
            />
          </div>
          <div className="mt-3 space-y-1">
            {searching && (
              <div className="text-sm text-slate-400 py-2 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Searching…
              </div>
            )}
            {!searching && query.trim() && results.length === 0 && (
              <div className="text-sm text-slate-400 py-2" data-testid="ms-no-results">
                No Format 1 record found. Create the certificate first.
              </div>
            )}
            {results.map((r) => (
              <button
                key={r.id}
                data-testid={`ms-result-${r.id}`}
                onClick={() => selectById(r.id)}
                className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-soft"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-900">{r.student_name}</span>
                  <span className="font-mono text-xs text-blue-800">{r.certificate_number}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Reg: {r.registration_number || "—"} · Enr: {r.enrollment_number || "—"} · {r.course_name}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {!loading && master && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* read-only student details from master record */}
            <section className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <UserCheck size={18} className="text-blue-800" /> Student details
                  <span className="text-xs font-normal text-slate-400">(from Format 1 record — locked)</span>
                </h3>
                {!sid && (
                  <button data-testid="ms-change-student"
                    onClick={() => { setMaster(null); setMarks([]); setExisting(null); }}
                    className="text-sm text-blue-800 hover:text-blue-900 font-medium">
                    Change student
                  </button>
                )}
              </div>
              {existing && (
                <div data-testid="ms-save-note"
                  className="mb-4 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
                  A marksheet already exists for this student — saving will update it (same QR & verification link).
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <Ro label="Student Name" value={master.student_name} testId="ms-ro-name" />
                <Ro label="Father's Name" value={master.father_name} testId="ms-ro-father" />
                <Ro label="Mother's Name" value={master.mother_name} testId="ms-ro-mother" />
                <Ro label="Session" value={master.session} testId="ms-ro-session" />
                <Ro label="Enrollment No." value={master.enrollment_number} testId="ms-ro-enrollment" />
                <Ro label="Registration No." value={master.registration_number} testId="ms-ro-registration" />
                <Ro label="Course" value={master.course_name} testId="ms-ro-course" />
                <Ro label="Course Duration" value={master.course_duration} testId="ms-ro-duration" />
                <Ro label="Certificate No." value={master.certificate_number} testId="ms-ro-certno" />
                <Ro label="Issue Date" value={master.issue_date} testId="ms-ro-issuedate" />
                <Ro label="Institute / Study Centre" value={master.institute_name} testId="ms-ro-institute" />
              </div>
              <p className="text-xs text-slate-400 mt-4">
                Wrong detail? Update it in the Format 1 master record — it will automatically reflect here.
              </p>
            </section>

            {/* marks entry */}
            <section className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Marks obtained</h3>
              <p className="text-xs text-slate-500 mb-4">
                Subjects, totals and minimum marks are pre-configured. Enter only the marks obtained.
              </p>
              {marks.length === 0 ? (
                <div className="text-sm text-slate-400 py-4" data-testid="ms-no-subjects">
                  No subjects configured for this course. Add subjects under Templates → Courses & Subjects.
                </div>
              ) : (
                [1, 2].map((term) => {
                  const rows = marks.filter((m) => (m.term ?? 1) === term);
                  if (!rows.length) return null;
                  return (
                    <div key={term}>
                      <div className="text-xs uppercase tracking-wider text-blue-800 font-semibold mt-3 mb-1"
                        data-testid={`ms-term-${term}`}>
                        Term {term}
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                            <th className="py-2 pr-2 font-medium">Subject</th>
                            <th className="py-2 px-2 font-medium w-20 text-center">Total</th>
                            <th className="py-2 px-2 font-medium w-20 text-center">Min.</th>
                            <th className="py-2 px-2 font-medium w-32">Marks obtained</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((m) => {
                            const i = marks.indexOf(m);
                            return (
                              <tr key={m.id} className="border-b border-slate-100">
                                <td className="py-2 pr-2" data-testid={`marks-subject-${i}`}>{m.subject}</td>
                                <td className="py-2 px-2 text-center text-slate-600" data-testid={`marks-total-${i}`}>{m.total}</td>
                                <td className="py-2 px-2 text-center text-slate-600" data-testid={`marks-min-${i}`}>{m.min}</td>
                                <td className="py-2 px-2">
                                  <Input
                                    data-testid={`marks-obtained-${i}`}
                                    type="number"
                                    value={m.obtained}
                                    min={0}
                                    max={m.total}
                                    onChange={(e) =>
                                      setMarks((ms) => ms.map((x) => (x.id === m.id ? { ...x, obtained: e.target.value } : x)))
                                    }
                                    className="h-9"
                                    autoFocus={i === 0}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })
              )}
              <div data-testid="marks-summary" className="mt-4 text-sm text-slate-700 border-t border-slate-100 pt-3">
                Grand total <b data-testid="marks-grand-total">{totals.t}</b> ·
                Obtained <b data-testid="marks-obtained-total">{totals.o}</b> ·
                Percentage <b data-testid="marks-percentage">{totals.pct}%</b> ·
                Grade <b data-testid="marks-grade">{totals.grade}</b> ·
                Result <b data-testid="marks-status" className={totals.status === "PASS" ? "text-green-700" : "text-red-600"}>{totals.status}</b>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Grade scale: {GRADE_SCALE_TEXT}. Result is FAIL if any subject is below its minimum marks.
              </p>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-xl border border-slate-200 bg-white p-6 sticky top-6">
              <div className="text-xs uppercase tracking-wider text-blue-800 font-semibold">
                Marksheet summary
              </div>
              <div className="font-serif-display text-2xl text-slate-900 mt-2 leading-snug">
                {master.student_name}
              </div>
              <div className="text-sm text-slate-500">
                {master.course_name} · {master.course_duration}
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <Ro label="Registration No." value={master.registration_number} />
                <Ro label="Certificate No." value={master.certificate_number} />
                <Ro label="Subjects" value={`${marks.length}`} />
                <Ro label="Total" value={`${totals.o} / ${totals.t}`} />
                <Ro label="Percentage" value={`${totals.pct}%`} />
                <Ro label="Grade" value={totals.grade} />
                <Ro label="Result" value={totals.status} />
              </div>
            </div>
          </aside>
        </div>
      )}

      <Dialog open={preview.open} onOpenChange={(v) => setPreview({ open: v, url: preview.url })}>
        <DialogContent className="max-w-4xl w-[90vw] h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Marksheet preview</DialogTitle>
          </DialogHeader>
          <iframe src={preview.url} title="preview" className="w-full flex-1 rounded border border-slate-200" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Ro = ({ label, value, testId }) => (
  <div className="flex justify-between gap-3">
    <span className="text-slate-500">{label}</span>
    <span data-testid={testId} className="font-medium text-slate-900 text-right">
      {value || "—"}
    </span>
  </div>
);
